/**
 * MemorySystemHealthCard — Displays overall memory system health status
 *
 * Shows:
 * - Overall status (pass/warn/fail)
 * - Individual health checks
 * - Issue details with recommendations
 */

import React, { useState } from 'react';
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2
} from 'lucide-react';

function MemorySystemHealthCard({ data, isLoading, error, onRefresh }) {
  const [expanded, setExpanded] = useState(true);

  if (isLoading) {
    return (
      <div className="md-card md-card--loading">
        <div className="md-card-header">
          <Activity size={18} />
          <span>System Health</span>
        </div>
        <div className="md-card-loading">
          <Loader2 size={24} className="md-spin" />
          <span>Loading health status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="md-card md-card--error">
        <div className="md-card-header">
          <Activity size={18} />
          <span>System Health</span>
          <button className="md-card-refresh" onClick={onRefresh}>
            <RefreshCw size={14} />
          </button>
        </div>
        <div className="md-card-error">
          <AlertTriangle size={20} />
          <span>Failed to load health status</span>
          <code>{error.message}</code>
        </div>
      </div>
    );
  }

  const status = data?.status || 'unknown';
  const checks = data?.checks || {};
  const issues = data?.issues || [];

  const getStatusIcon = (s) => {
    switch (s) {
      case 'pass':
      case 'healthy':
        return <CheckCircle2 size={16} className="md-status-icon md-status-icon--pass" />;
      case 'warn':
      case 'warning':
        return <AlertTriangle size={16} className="md-status-icon md-status-icon--warn" />;
      case 'fail':
      case 'error':
        return <XCircle size={16} className="md-status-icon md-status-icon--fail" />;
      default:
        return <Activity size={16} className="md-status-icon md-status-icon--unknown" />;
    }
  };

  const getStatusClass = (s) => {
    if (['pass', 'healthy'].includes(s)) return 'md-card--pass';
    if (['warn', 'warning'].includes(s)) return 'md-card--warn';
    if (['fail', 'error'].includes(s)) return 'md-card--fail';
    return '';
  };

  return (
    <div className={`md-card ${getStatusClass(status)}`}>
      <div
        className="md-card-header md-card-header--clickable"
        onClick={() => setExpanded(!expanded)}
      >
        <Activity size={18} />
        <span>System Health</span>
        <div className="md-card-header-right">
          {getStatusIcon(status)}
          <span className={`md-status-badge md-status-badge--${status}`}>
            {status.toUpperCase()}
          </span>
          <button className="md-card-refresh" onClick={(e) => { e.stopPropagation(); onRefresh(); }}>
            <RefreshCw size={14} />
          </button>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="md-card-content">
          {/* Health Checks Grid */}
          <div className="md-checks-grid">
            {Object.entries(checks).map(([key, check]) => (
              <div key={key} className={`md-check-item md-check-item--${check.status}`}>
                {getStatusIcon(check.status)}
                <div className="md-check-info">
                  <span className="md-check-name">{formatCheckName(key)}</span>
                  <span className="md-check-value">{check.value ?? check.message ?? '-'}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Issues List */}
          {issues.length > 0 && (
            <div className="md-issues">
              <h4 className="md-issues-title">
                <AlertTriangle size={14} />
                Issues Detected ({issues.length})
              </h4>
              <ul className="md-issues-list">
                {issues.map((issue, idx) => (
                  <li key={idx} className={`md-issue md-issue--${issue.severity || 'warn'}`}>
                    <span className="md-issue-message">{issue.message}</span>
                    {issue.recommendation && (
                      <span className="md-issue-recommendation">{issue.recommendation}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* All Clear Message */}
          {issues.length === 0 && status === 'pass' && (
            <div className="md-all-clear">
              <CheckCircle2 size={20} />
              <span>All systems operational</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatCheckName(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

export default MemorySystemHealthCard;
